import { Directive, ElementRef, HostListener, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

export function formatAmountWithCommas(
  value: number | string | null | undefined,
): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const [integerPart, decimalPart] = value
    .toString()
    .replace(/,/g, "")
    .split(".");
  const formattedInteger = (integerPart || "").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ",",
  );
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
}

export function parseFormattedAmount(
  value: string | number | null | undefined,
): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = parseFloat(value.toString().replace(/,/g, ""));
  return isNaN(parsed) ? null : parsed;
}

/**
 * Attach to any text input bound with formControlName/ngModel to display the
 * typed value with thousand-separator commas while the underlying model
 * keeps holding a plain number (e.g. for validators, math, and API payloads).
 * Usage: <input type="text" inputmode="decimal" appFormatAmount formControlName="amount" />
 */
@Directive({
  selector: "input[appFormatAmount]",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormatAmountDirective),
      multi: true,
    },
  ],
})
export class FormatAmountDirective implements ControlValueAccessor {
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>) {}

  @HostListener("input", ["$event.target.value"])
  handleInput(value: string): void {
    this.el.nativeElement.value = formatAmountWithCommas(value);
    this.onChange(parseFormattedAmount(value));
  }

  @HostListener("blur")
  handleBlur(): void {
    this.onTouched();
  }

  writeValue(value: number | string | null): void {
    this.el.nativeElement.value = formatAmountWithCommas(value);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }
}
